import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import {
  Button,
  Dropdown,
  Flex,
  Grid,
  Heading,
  IsVisible,
  Text,
} from '@packages/ui';
import { Url } from 'next/dist/shared/lib/router/router';
import Link from 'next/link';
import { IconType } from 'react-icons';
import { FiChevronRight } from 'react-icons/fi';
import { twMerge } from 'tailwind-merge';

import { EInterventionStatus } from 'enums/intervention';

import { dropdownStyles } from './styles';

interface IComponentDropdownSubOptions {
  icon?: IconType;
  interventionStatus?: EInterventionStatus;
  isVisible?: boolean;
  label: string;
  onClick?: () => void;
}

export interface IComponentDropdownOptions {
  href?: Url;
  icon?: IconType;
  isVisible?: boolean;
  label: string;
  onClick?: () => void;
  prefetch?: boolean;
  scroll?: boolean;
  subOptions?: IComponentDropdownSubOptions[];
}

interface IComponentDropdownProps {
  className?: string;
  dictionary?: any;
  dropdownOptions: IComponentDropdownOptions[];
  position?:
    | 'top'
    | 'center'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right';
  username?: string;
}

export interface IComponentDropdownRefProps {
  open: () => void;
}

const ComponentDropdown: React.ForwardRefRenderFunction<
  IComponentDropdownRefProps,
  IComponentDropdownProps
> = function ComponentDropdown(
  { className, dictionary, dropdownOptions, position, username = '' },
  ref,
) {
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [isSubOptionOpened, setIsSubOptionOpened] = useState<boolean>(false);

  const openDropdown = () => {
    setIsOpened(true);
  };

  const handleCloseDropdown = useCallback(
    (event: any) => {
      if (isOpened && !event.target?.closest('.dropdownArea')) {
        setIsOpened(false);
      }
    },
    [isOpened],
  );

  const handleCloseOnClick = (optionAction?: () => void) => {
    if (optionAction) {
      optionAction();
    }
    setIsOpened(false);
  };

  const onMouseEnter = (hasSubOptions: boolean) => {
    if (hasSubOptions) {
      setIsSubOptionOpened(true);
    } else {
      setIsSubOptionOpened(false);
    }
  };

  const onMouseLeave = () => {
    setIsSubOptionOpened(false);
  };

  useEffect(() => {
    document.addEventListener('click', handleCloseDropdown);

    return () => {
      document.removeEventListener('click', handleCloseDropdown);
    };
  }, [handleCloseDropdown]);

  useImperativeHandle(ref, () => ({
    open: openDropdown,
  }));

  return (
    <Dropdown
      className={twMerge(
        'rounded py-2 top-[calc(100%-.5rem)] dropdownArea overflow-visible',
        className,
      )}
      design="europeAssistance"
      isOpened={isOpened}
      position={position}
    >
      <IsVisible when={!!username}>
        <Heading
          className="font-medium text-base text-gray-400 px-4 py-2"
          variant="h6"
        >
          {dictionary?.dropdown?.welcome || 'Welcome'},{' '}
          <Text className="font-bold text-gray-400" variant="b">
            {username}
          </Text>
        </Heading>
      </IsVisible>
      {dropdownOptions.map(
        ({
          icon: Icon,
          isVisible = true,
          subOptions = [],
          ...dropdownOption
        }) => (
          <Flex key={dropdownOption.label}>
            <IsVisible when={isVisible}>
              <IsVisible when={!!dropdownOption.href}>
                <Link
                  className="flex items-center justify-start h-9 w-full px-4 text-sm font-medium rounded-none text-gray-700 hover:opacity-60 uppercase"
                  href={dropdownOption.href as Url}
                  onClick={() => handleCloseOnClick(dropdownOption.onClick)}
                  onMouseEnter={() => onMouseEnter(subOptions.length > 0)}
                  prefetch={dropdownOption.prefetch}
                  scroll={dropdownOption.scroll}
                >
                  {Icon && <Icon />}
                  {dropdownOption.label}
                </Link>
              </IsVisible>
              <IsVisible when={!dropdownOption.href}>
                <Button
                  className="w-full justify-start font-medium rounded-none h-9 text-gray-700"
                  design="transparent"
                  onClick={() => handleCloseOnClick(dropdownOption.onClick)}
                  onMouseEnter={() => onMouseEnter(subOptions.length > 0)}
                >
                  {Icon && <Icon />}
                  {dropdownOption.label}
                  <IsVisible when={subOptions.length > 0}>
                    <FiChevronRight size={14} />
                  </IsVisible>
                </Button>
              </IsVisible>
              <IsVisible when={subOptions.length > 0}>
                <Dropdown
                  className="rounded py-2 top-full"
                  isOpened={isSubOptionOpened}
                  position="top-right"
                >
                  <Grid className="relative" onMouseLeave={onMouseLeave}>
                    {subOptions.map(
                      ({ icon: SubIcon, isVisible = true, ...subOption }) => (
                        <IsVisible key={subOption.label} when={isVisible}>
                          <Button
                            className={dropdownStyles.subOptionButton({
                              interventionStatus:
                                subOption?.interventionStatus as EInterventionStatus,
                            })}
                            design="transparent"
                            onClick={() =>
                              handleCloseOnClick(subOption.onClick)
                            }
                          >
                            {SubIcon && <SubIcon />}
                            {subOption.label}
                          </Button>
                        </IsVisible>
                      ),
                    )}
                  </Grid>
                </Dropdown>
              </IsVisible>
            </IsVisible>
          </Flex>
        ),
      )}
    </Dropdown>
  );
};

export default forwardRef(ComponentDropdown);
